/**
 * Audit Log Detail Modal Component
 *
 * Displays comprehensive details of a single audit log entry.
 * Includes JSON viewers for request/response data and copy functionality.
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { AuditLog } from '@/types/audit-log';
import {
  AUDIT_LOG_CATEGORY_COLORS,
  AUDIT_LOG_CATEGORY_LABELS,
  AUDIT_LOG_SEVERITY_COLORS,
  AUDIT_LOG_SEVERITY_LABELS,
  CACHE_STATUS_COLORS,
  CACHE_STATUS_LABELS,
  getStatusCodeColor,
} from '@/types/audit-log';
import { cn } from '@/lib/utils';

/**
 * Audit Log Detail Modal Props
 */
interface AuditLogDetailModalProps {
  /** Audit log entry to display */
  log: AuditLog | null;
  /** Modal open state */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
}

/**
 * Format Date Time
 */
function formatDateTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'MMMM dd, yyyy HH:mm:ss');
  } catch {
    return dateString;
  }
}

/**
 * JSON Viewer Component
 * Collapsible JSON display with syntax highlighting
 */
function JSONViewer({
  data,
  title,
}: {
  data: Record<string, unknown> | null | undefined;
  title: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors"
      >
        <span className="font-medium text-sm">{title}</span>
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {isExpanded && (
        <div className="p-3 bg-muted/20">
          <pre className="text-xs overflow-x-auto">
            <code>{JSON.stringify(data, null, 2)}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Detail Row Component
 */
function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) {
  if (value === null || value === undefined) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-2 border-b last:border-0">
      <dt className="text-sm font-medium text-muted-foreground w-full sm:w-1/3">{label}</dt>
      <dd className={cn('text-sm w-full sm:w-2/3', mono && 'font-mono text-xs break-all')}>
        {value}
      </dd>
    </div>
  );
}

/**
 * Audit Log Detail Modal Component
 */
export function AuditLogDetailModal({ log, isOpen, onClose }: AuditLogDetailModalProps) {
  const [copied, setCopied] = useState(false);

  if (!log) {
    return null;
  }

  /**
   * Copy Log to Clipboard
   * Copies entire log as formatted JSON
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(log, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error: unknown) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-xl mb-2">{log.eventType}</DialogTitle>
              <p className="text-sm text-muted-foreground">{formatDateTime(log.occurredAt)}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={copied}>
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy JSON
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <ScrollArea className="h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Overview Section */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Overview</h3>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Badge
                    variant="outline"
                    className={cn('font-medium', AUDIT_LOG_CATEGORY_COLORS[log.category])}
                  >
                    {AUDIT_LOG_CATEGORY_LABELS[log.category]}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn('font-medium', AUDIT_LOG_SEVERITY_COLORS[log.severity])}
                  >
                    {AUDIT_LOG_SEVERITY_LABELS[log.severity]}
                  </Badge>
                </div>
                <dl className="divide-y border rounded-lg">
                  <DetailRow label="Log ID" value={log.id} mono />
                  <DetailRow label="Action" value={log.action} />
                  <DetailRow label="User ID" value={log.userId} mono />
                  <DetailRow label="Admin ID" value={log.adminId} mono />
                  <DetailRow label="IP Address" value={log.ipAddress} mono />
                  <DetailRow label="User Agent" value={log.userAgent} mono />
                </dl>
              </div>
            </section>

            {/* HTTP Request Info */}
            {(log.httpMethod || log.endpoint || log.statusCode || log.responseTimeMs) && (
              <section>
                <h3 className="text-lg font-semibold mb-3">HTTP Request</h3>
                <dl className="divide-y border rounded-lg">
                  <DetailRow label="Method" value={log.httpMethod} />
                  <DetailRow label="Endpoint" value={log.endpoint} mono />
                  {log.statusCode && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-2 border-b">
                      <dt className="text-sm font-medium text-muted-foreground w-full sm:w-1/3">
                        Status Code
                      </dt>
                      <dd className="w-full sm:w-2/3">
                        <span
                          className={cn(
                            'text-sm font-mono font-medium',
                            getStatusCodeColor(log.statusCode)
                          )}
                        >
                          {log.statusCode}
                        </span>
                      </dd>
                    </div>
                  )}
                  <DetailRow
                    label="Response Time"
                    value={log.responseTimeMs ? `${log.responseTimeMs} ms` : undefined}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-2">
                    <dt className="text-sm font-medium text-muted-foreground w-full sm:w-1/3">
                      Cache Status
                    </dt>
                    <dd className="w-full sm:w-2/3">
                      <Badge
                        variant="outline"
                        className={cn(
                          'font-medium text-xs',
                          log.cacheStatus
                            ? CACHE_STATUS_COLORS[log.cacheStatus]
                            : CACHE_STATUS_COLORS['na']
                        )}
                      >
                        {log.cacheStatus
                          ? CACHE_STATUS_LABELS[log.cacheStatus]
                          : CACHE_STATUS_LABELS['na']}
                      </Badge>
                    </dd>
                  </div>
                </dl>
              </section>
            )}

            {/* Resource Information */}
            {(log.resourceType || log.resourceId) && (
              <section>
                <h3 className="text-lg font-semibold mb-3">Resource Information</h3>
                <dl className="divide-y border rounded-lg">
                  <DetailRow label="Resource Type" value={log.resourceType} />
                  <DetailRow label="Resource ID" value={log.resourceId} mono />
                </dl>
              </section>
            )}

            {/* Request Data */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Request & Response Data</h3>
              <div className="space-y-3">
                <JSONViewer data={log.requestData} title="Request Data" />
                <JSONViewer data={log.responseData} title="Response Data" />
                <JSONViewer data={log.metadata} title="Metadata" />
              </div>
            </section>

            {/* Change Tracking */}
            {(log.oldValue || log.newValue) && (
              <section>
                <h3 className="text-lg font-semibold mb-3">Change Tracking</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {log.oldValue && (
                    <div>
                      <JSONViewer data={log.oldValue} title="Old Value" />
                    </div>
                  )}
                  {log.newValue && (
                    <div>
                      <JSONViewer data={log.newValue} title="New Value" />
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Session Information */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Session Information</h3>
              <dl className="divide-y border rounded-lg">
                <DetailRow label="Session ID" value={log.sessionId} mono />
                <DetailRow label="Correlation ID" value={log.correlationId} mono />
              </dl>
            </section>

            {/* Timestamps */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Timestamps</h3>
              <dl className="divide-y border rounded-lg">
                <DetailRow label="Occurred At" value={formatDateTime(log.occurredAt)} />
                {log.archivedAt && (
                  <DetailRow label="Archived At" value={formatDateTime(log.archivedAt)} />
                )}
              </dl>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
